<script>
import _ from 'lodash'
import MottoIcon from '@components/motto-icon.vue'
import { authComputed, onboardingComputed } from '@state/helpers'
import SidebarItem from './sidebar-item.vue'

export default {
  components: {
    SidebarItem,
    MottoIcon,
  },
  props: {
    sidebarExpanded: {
      type: Boolean,
    },
  },
  data() {
    return {
      sidebarRoutes: [
        {
          name: 'home',
          title: 'Home',
        },
        {
          name: 'onboarding',
          title: 'Resume Onboarding',
          icon: 'BuildingCircleExclamation',
        },
        {
          name: 'apps',
          title: 'Apps',
        },
        {
          name: 'education',
          title: 'Education',
          routeLabel: 'education-group',
          icon: 'Book',
        },
        {
          name: 'educationDemo',
          title: 'Education Demo',
          icon: 'Book',
        },
        {
          name: 'onlinePresence',
          title: 'Online Presence',
          icon: 'PeopleGroup',
        },
        {
          name: 'resources',
          title: 'Resources',
        },
        {
          name: 'myInfo',
          title: 'Office Management',
          icon: 'UsersTrio',
          alias: 'my-office',
        },
        {
          name: 'leaderboards',
          title: 'Leaderboards',
          routeLabel: 'leaderboards-group',
          icon: 'Awards',
        },
        {
          name: 'network-connect',
          title: 'Network Connect',
          icon: 'Connection',
        },
      ],
      sidebarSubRoutes: [
        {
          name: 'leaderboards',
          title: 'Leaderboards',
          parentRouteLabel: 'leaderboards-group',
        },
        {
          name: 'myRankings',
          title: 'My Rankings',
          parentRouteLabel: 'leaderboards-group',
        },
        {
          name: 'awards',
          title: 'Awards',
          parentRouteLabel: 'leaderboards-group',
        },
        {
          name: 'my-awards',
          title: 'My Awards',
          parentRouteLabel: 'leaderboards-group',
        },
        {
          name: 'education',
          title: 'Education',
          parentRouteLabel: 'education-group',
        },
        {
          name: 'assign-courses',
          title: 'Start a New Course',
          parentRouteLabel: 'education-group',
        },
        {
          name: 'my-courses',
          title: 'My Courses',
          parentRouteLabel: 'education-group',
        },
      ],
      sidebarOffsetY: 0,
    }
  },
  computed: {
    ...authComputed,
    ...onboardingComputed,
    routeSidebarItemName() {
      return this.$route.meta?.sidebarItemName
    },
    routeSubSidebarItemName() {
      return this.$route.meta?.subSidebarItemName
    },
    getSidebarRoutes() {
      let sidebarRoutes = this.sidebarRoutes
      if (process.env.APP_ENV === 'prod') {
        sidebarRoutes = sidebarRoutes.filter((route) => !route.devOnly)
      }

      if (!this.canManageNetworkConnect) {
        sidebarRoutes = sidebarRoutes.filter((route) => route.name !== 'network-connect')
      }

      if (!this.canAccessEducation) {
        sidebarRoutes = sidebarRoutes.filter((route) => route.name !== 'education')
      }

      if (!this.canAccessEducationDemo) {
        sidebarRoutes = sidebarRoutes.filter((route) => route.name !== 'educationDemo')
      }

      if (!this.canAccessOnlinePresence) {
        sidebarRoutes = sidebarRoutes.filter((route) => route.name !== 'onlinePresence')
      }

      if (!this.isBoOrOpsManager) {
        sidebarRoutes = sidebarRoutes.filter((route) => route.name !== 'myInfo')
      }

      if (!this.canAccessOnboarding || !this.hasPendingOnboarding) {
        sidebarRoutes = sidebarRoutes.filter((route) => route.name !== 'onboarding')
      }

      return [...sidebarRoutes]
    },
    getSidebarSubRoutes() {
      return this.sidebarSubRoutes.map((subRoute) => {
        if (subRoute.name === 'assign-courses' && this.isBoOrOpsManager) {
          return { ...subRoute, title: 'Assign Courses' }
        }

        return subRoute
      })
    },
  },
  methods: {
    getSubRoutes({ routeLabel }) {
      return _.filter(this.getSidebarSubRoutes, { parentRouteLabel: routeLabel })
    },
  },
}
</script>

<template>
  <div
    :class="{
      [$style.sidebarContainer]: true,
      [$style.isExpanded]: sidebarExpanded,
    }">
    <Transition name="fade">
      <div v-if="sidebarExpanded" :class="$style.expandBackground"></div>
    </Transition>
    <div
      :class="{
        [$style.container]: true,
        [$style.sidebarExpanded]: sidebarExpanded,
      }"
      data-sidebar>
      <div :class="$style.logoContainer">
        <BaseLink href="/" target="_self">
          <img :class="$style.sidebarLogo" src="@assets/images/mottocenter-new-logo-blue.png" alt="Logo" />
          <img :class="$style.sidebarLogoMobile" src="@assets/images/mottocenter-new-logo-blue.png" alt="Logo" />
        </BaseLink>
        <MottoIcon
          :class="$style.mobileHamburgerClose"
          name="HamburgerClose"
          icon-color="primary"
          @click.native="$emit('collapse')" />
      </div>
      <div style="margin-left: 24px">
        <SidebarItem
          v-for="sidebarRoute in getSidebarRoutes"
          :key="sidebarRoute.title"
          :route="sidebarRoute"
          :label="sidebarRoute.title"
          :icon="sidebarRoute.icon || sidebarRoute.title"
          :is-new="sidebarRoute.isNew"
          :active="sidebarRoute.title === routeSidebarItemName"
          :selected-sub-sidebar-item="routeSubSidebarItemName"
          :sub-routes="getSubRoutes(sidebarRoute)" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.sidebarContainer {
  @media (max-width: $lg-breakpoint) {
    position: absolute;
    z-index: 10000;
    pointer-events: none;
  }

  z-index: 100;
  pointer-events: auto;
}

.isExpanded {
  pointer-events: auto;
}

.container {
  @media (max-width: $lg-breakpoint) {
    z-index: 100;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    height: 100vh;
    border: none;
    transition: all 0.3s linear;
    transform: translateX(-300px);
    &.sidebarExpanded {
      position: fixed;
      min-width: 263px;
      max-width: 263px;
      overflow-y: auto;
      transition: all 0.3s linear;
      transform: translateX(0);
    }
  }

  position: fixed;
  z-index: 100;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  width: 273px;
  max-width: 273px;
  height: calc(100% - 20px);
  min-height: calc(100vh - 20px);
  padding-bottom: 20px;
  margin-bottom: 20px;
  overflow: hidden;
  overflow-y: auto;
  background: $lightest-gray;
  border: 1px solid $lightest-gray;
  border-radius: 0 0 56px 0;
  box-shadow: 0 0 24px rgba(196, 196, 196, 0.3);
}

.logoContainer {
  display: flex;
  justify-content: space-between;

  a {
    @media (max-width: $lg-breakpoint) {
      margin: 0;
    }

    margin-left: 50px;
  }
}

.mobileHamburgerClose {
  @media (max-width: $lg-breakpoint) {
    display: block;
    margin-top: 33px;
    margin-right: 29px;
  }

  display: none;
}

.sidebarLogoMobile {
  @media (max-width: $lg-breakpoint) {
    display: block;
    width: 128px;
    height: 24px;
    margin-top: 33px;
    margin-bottom: 56px;
    margin-left: 24px;
  }

  display: none;
}

.sidebarLogo {
  @media (max-width: $lg-breakpoint) {
    display: none;
  }

  display: block;
  width: 170px;
  height: 32px;
  margin: 33px auto 56px;
}

.expandBackground {
  @media (max-width: $lg-breakpoint) {
    position: fixed;
    z-index: 90;
    display: block;
    width: 100%;
    height: 100%;
    background: rgba($primary-charcoal, 0.5);
  }

  display: none;
}

.php {
  position: absolute;
  font-size: 20px;
}
</style>
